import React from "react";
import { Instagram, Mail, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { useLang } from "@/i18n";
import { CONTACT_EMAIL, INSTAGRAM_URL } from "@/lib/config";
import { breadcrumbLd, organizationLd, pageLd } from "@/lib/seo";

const Contacto = () => {
  const { lang, t } = useLang();

  return (
    <div className="bg-[#F5F1E8] overflow-x-hidden">
      <Seo
        title={t.seo.contacto.title}
        description={t.seo.contacto.description}
        path="/contacto"
        lang={lang}
        jsonLd={[
          organizationLd(),
          breadcrumbLd([
            { name: "Naguarà", path: "/" },
            { name: t.nav.contact, path: "/contacto" },
          ]),
          pageLd("ContactPage", {
            name: t.contacto.title,
            description: t.seo.contacto.description,
            path: "/contacto",
          }),
        ]}
      />
      <Navbar />
      <div className="h-20 bg-[#422D28]" aria-hidden="true" />

      <main data-testid="page-contacto" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <Reveal>
              <p className="text-[#3B2523]/70 uppercase tracking-[0.25em] text-xs font-semibold">
                {t.contacto.tag}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#3B2523] mt-5 leading-[1.02]">
                {t.contacto.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#3B2523]/70 text-base md:text-lg mt-6 leading-relaxed max-w-[560px]">
                {t.contacto.intro}
              </p>
            </Reveal>

            <h2 className="font-display text-xl md:text-2xl font-bold text-[#3B2523] mt-12">
              {t.contacto.dataTitle}
            </h2>

            <dl className="mt-6 space-y-8">
              <div>
                <dt className="flex items-center gap-2 text-[#3B2523]/70 uppercase tracking-[0.18em] text-xs font-semibold">
                  <Mail size={14} />
                  {t.contacto.emailTitle}
                </dt>
                <dd className="mt-2">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    data-testid="contacto-email"
                    className="inline-flex items-center min-h-[44px] font-display text-xl md:text-2xl font-bold text-[#3B2523] underline decoration-[#F4D35E] decoration-4 underline-offset-[6px] hover:decoration-[#3B2523] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523] focus-visible:ring-offset-2 rounded"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </dd>
              </div>

              <div>
                <dt className="flex items-center gap-2 text-[#3B2523]/70 uppercase tracking-[0.18em] text-xs font-semibold">
                  <Instagram size={14} />
                  {t.contacto.igTitle}
                </dt>
                <dd className="mt-2">
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contacto-instagram"
                    className="inline-flex items-center min-h-[44px] text-[#3B2523] font-semibold hover:text-[#8C6B2F] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523] focus-visible:ring-offset-2 rounded"
                  >
                    @naguracafes
                  </a>
                </dd>
              </div>

              <div>
                <dt className="flex items-center gap-2 text-[#3B2523]/70 uppercase tracking-[0.18em] text-xs font-semibold">
                  <MapPin size={14} />
                  {t.contacto.locationTitle}
                </dt>
                <dd data-testid="contacto-location" className="mt-2 text-[#3B2523] font-semibold">
                  {t.footer.location}
                </dd>
                <dd className="mt-1 text-[#3B2523]/70 text-sm max-w-[420px] leading-relaxed">
                  {t.contacto.locationNote}
                </dd>
              </div>
            </dl>
          </div>

          <Reveal delay={0.1}>
            <ContactForm kind="general" tone="light" testId="contacto-form" id="formulario" />
          </Reveal>
        </div>
      </main>

      <Footer />
      <CartDrawer />
    </div>
  );
};

export default Contacto;
